import type { CSSProperties } from 'react';

import { X } from '@phosphor-icons/react';
import classNames from 'classnames';

import SimpleModal from '../ui/SimpleModal';

import { cn } from '../utils/cn';

import { signupModalContentContainer, signupModalDialogContent } from './SignupModal.css';
import type { SignupModalElementProps } from './SignupModal.type';

export function SignupModalElement(props: SignupModalElementProps) {
  const { element, children, isOpen = false, onOpenChange, isPreviewing = false } = props;
  const {
    gap,
    width,
    maxWidth,
    mobileWidth,
    mobileMaxWidth,
    padding,
    backgroundColor,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    modalOverlayBackgroundColor,
    modalCloseButtonColor = 'inherit',
    modalCloseButtonPlacementTop,
    modalCloseButtonPlacementRight,
  } = element.attrs || {};

  const renderedElement = (
    <div
      className={classNames(signupModalContentContainer, 'flex flex-col h-fit')}
      style={
        {
          backgroundColor,
          padding,
          borderStyle,
          borderWidth,
          borderRadius,
          borderColor,
          gap,
          '--width': width,
          '--max-width': maxWidth,
          '--mobile-width': mobileWidth,
          '--mobile-max-width': mobileMaxWidth,
          '--padding': padding,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );

  const generatedStyle = {
    '--overlay-background-color': modalOverlayBackgroundColor,
    '--modal-close-button-color': modalCloseButtonColor,
    '--modal-close-button-placement-top': modalCloseButtonPlacementTop || '24px',
    '--modal-close-button-placement-right': modalCloseButtonPlacementRight || '24px',
  } as CSSProperties;

  if (isPreviewing) {
    return (
      <div className="w-full h-auto min-h-[600px] flex items-center justify-center relative" style={generatedStyle}>
        <div className="absolute top-0 left-0 w-full h-full bg-[--overlay-background-color]">&nbsp;</div>
        <div className="z-10 relative w-full md:w-auto">
          <div
            className={classNames(signupModalContentContainer, 'flex flex-col h-fit relative')}
            style={
              {
                backgroundColor,
                padding,
                borderStyle,
                borderWidth,
                borderRadius,
                borderColor,
                gap,
                '--width': width,
                '--max-width': maxWidth,
                '--mobile-width': mobileWidth,
                '--mobile-max-width': mobileMaxWidth,
                '--padding': padding,
              } as CSSProperties
            }
          >
            {children}
            <button
              type="button"
              className={cn(
                'absolute right-[--modal-close-button-placement-right] top-[--modal-close-button-placement-top] rounded-sm opacity-70',
                'hover:opacity-100 transition-opacity',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'text-[--modal-close-button-color]',
              )}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SimpleModal isOpen={isOpen} onClose={onOpenChange} style={generatedStyle} className="shadow-lg">
      <div className={classNames(signupModalDialogContent)}>{renderedElement}</div>
    </SimpleModal>
  );
}
